/**
 * To make a set of checkboxes that can be {,de}selected all at once, either with buttons,
 * or parent checkboxes, you can:
 *
 *  1) Mark a common parent between all of the checkboxes and the controlling
 *     buttons/checkboxes with the attribute `data-checkbox-grid`.
 *
 *  2) Mark each element you want to control the checkboxes with one of:
 *    - `data-select-all` to mark a button as something that will turn on *all*
 *      checkboxes under the common parent, or to mark a checkbox as a "toggle all".
 *    - `data-clear` to mark a button as something that will turn *off* all checkboxes.
 *
 *  3) Mark any element under the common parent that you wish to keep a count of the
 *     active checkboxes with a `data-checkbox-counter` attribute.
 */
document.addEventListener('DOMContentLoaded', () => {
  /**
   * Sets a list of checkboxes to checked, unchecked, or with a line through
   * it (indeterminate).
   * @param {NodeList} checkboxes - A list of checkboxes to set.
   * @param {boolean | 'indeterminate'} checkboxState - `true`, `false`, or the string,
   * `'indeterminate'` to set all of the checkboxes to checked, unchecked, or with a line!
   */
  const setCheckboxes = (checkboxes, checkboxState) => {
    if (checkboxState == 'indeterminate') {
      checkboxes.forEach((e) => {
        e.indeterminate = true;
        e.checked = true;
      });
    } else {
      checkboxes.forEach((e) => {
        e.indeterminate = false;
        e.checked = checkboxState;
      });
    }
  };

  document.querySelectorAll('[data-checkbox-grid]').forEach((element) => {
    const clearAllButtons = element.querySelectorAll('[data-clear]');
    const selectAllButtons = element.querySelectorAll('[data-select-all]:not([type=checkbox])');
    const toggleAllChecks = element.querySelectorAll('[data-select-all][type=checkbox]');

    const toggleAll = (value) =>
      // Not lambda syntax because context propagation (`this`) is important for checkboxes
      function () {
        // Intentionally including [data-select-all] checkboxes so they also turn on/off with buttons.
        // Also, re-querying each time to gravefully handle DOM mutation
        setCheckboxes(element.querySelectorAll('input[type=checkbox]'), value || this.checked);
      };

    // Select all / none element events
    selectAllButtons.forEach((e) => e.addEventListener('click', toggleAll(true)));
    clearAllButtons.forEach((e) => e.addEventListener('click', toggleAll(false)));
    toggleAllChecks.forEach((e) => e.addEventListener('change', toggleAll()));

    // All other state updates that should result in element updates
    element.querySelectorAll('input[type=checkbox]').forEach((e, _, all) =>
      e.addEventListener('change', () => {
        const selectable = Array.from(all).filter((e) => !e.hasAttribute('data-select-all'));
        const checked = selectable.filter((e) => e.checked);

        // Update elements that want counts!
        element.querySelectorAll('[data-checkbox-counter]').forEach((e) => {
          e.textContent = checked.length;
        });

        // Show/hide elements that are only visible when a selection exists
        // (default with at least 1 selection, but can be configured with the
        // data attribute)
        element.querySelectorAll('[data-show-on-select]').forEach((e) => {
          e.classList.toggle('invisible', checked.length < (e.dataset.showOnSelect || 0));
        });

        // Set "select all" checkboxes to appropriate states
        switch (checked.length) {
          // All checks! Select all boxes are checked
          case selectable.length:
            return setCheckboxes(toggleAllChecks, true);
          // No checks! Select all boxes are unchecked
          case 0:
            return setCheckboxes(toggleAllChecks, false);
          // Some checks; select boxes have a line through em
          default:
            return setCheckboxes(toggleAllChecks, 'indeterminate');
        }
      }),
    );
  });
});
